import React, { useEffect, useState } from 'react';
import { Icon } from '../components/Icons.jsx';
import { api } from '../services/api/client.js';
import { formatAppDate } from '../utils/locale.js';

const FAQ_CATEGORIES = [
  { id: 'all', label: 'All articles', icon: 'grid' },
  { id: 'getting', label: 'Getting started', icon: 'sparkles' },
  { id: 'billing', label: 'Plan & usage', icon: 'creditCard' },
  { id: 'social', label: 'Social accounts', icon: 'link' },
  { id: 'ai', label: 'AI & content', icon: 'autopilot' },
  { id: 'account', label: 'Account & security', icon: 'lock' },
];

const FAQS = [
  { cat: 'getting', q: 'How does Autopilot work?', a: 'Autopilot uses your Brand Profile to generate posts on your schedule. You can approve drafts first or let it publish automatically once your rules are ready.' },
  { cat: 'billing', q: 'What am I paying for?', a: 'Plans include concrete monthly output limits: publish-ready posts, premium AI visuals, brands, social accounts, and Assistant fair use.' },
  { cat: 'billing', q: 'Can I cancel my subscription?', a: 'Yes. Go to Plan & Usage, open Payments, and manage your plan. Access remains active until the end of the billing period.' },
  { cat: 'social', q: 'Why does my Instagram token expire?', a: 'Meta requires periodic reauthorization. Dathent shows a reconnect state before the token stops publishing.' },
  { cat: 'social', q: 'How does Telegram publishing work?', a: 'Start the Dathent bot, then add it to a Telegram channel as an admin with post permissions. Connected status appears only after the real channel is linked.' },
  { cat: 'ai', q: 'Can I edit generated posts?', a: 'Yes. Every draft can be edited before scheduling or publishing. You can also regenerate selected sections.' },
  { cat: 'account', q: 'Is my data secure?', a: 'Dathent uses session cookies for the app and OAuth for platform access. Platform passwords are never stored by the app.' },
];

const TONES = {
  green: ['var(--green-soft)', 'var(--green)'],
  purple: ['rgba(124,92,255,0.14)', 'var(--p-300)'],
  amber: ['var(--amber-soft)', 'var(--amber)'],
  red: ['var(--red-soft)', 'var(--red)'],
  blue: ['var(--blue-soft)', 'var(--blue)'],
};

export function HelpCenter() {
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);

  const counts = FAQ_CATEGORIES.reduce((acc, item) => {
    acc[item.id] = item.id === 'all' ? FAQS.length : FAQS.filter(faq => faq.cat === item.id).length;
    return acc;
  }, {});
  const normalizedSearch = search.trim().toLowerCase();
  const visibleFaqs = FAQS
    .filter(faq => category === 'all' || faq.cat === category)
    .filter(faq => !normalizedSearch || `${faq.q} ${faq.a}`.toLowerCase().includes(normalizedSearch));

  return (
    <div className="page help-page">
      <div className="help-shell">
        <aside className="col" style={{ gap: 4 }}>
          <div className="eyebrow" style={{ padding: '8px 12px' }}>Categories</div>
          {FAQ_CATEGORIES.map(item => (
            <button key={item.id} className={'help-cat ' + (category === item.id ? 'is-active' : '')} onClick={() => setCategory(item.id)}>
              <Icon name={item.icon} size={14} />
              <span>{item.label}</span>
              <span className="help-cat-count">{counts[item.id]}</span>
            </button>
          ))}
          <div className="card" style={{ padding: 14, marginTop: 16, background: 'linear-gradient(180deg, rgba(124,92,255,0.10), rgba(124,92,255,0.02))' }}>
            <div className="row" style={{ gap: 8, marginBottom: 6 }}>
              <div className="type-icon" style={{ width: 28, height: 28 }}><Icon name="sparkles" size={14} /></div>
              <div className="h3">Still need help?</div>
            </div>
            <p className="caption" style={{ marginBottom: 10 }}>Send context to support from inside the app.</p>
            <button className="btn btn-primary btn-block btn-sm" onClick={() => setSupportOpen(true)}>Contact support</button>
          </div>
        </aside>

        <section>
          <h2 className="h2" style={{ marginBottom: 8 }}>Frequently asked questions</h2>
          <p className="caption" style={{ marginBottom: 20 }}>Search the knowledge base or browse by category.</p>
          <div style={{ position: 'relative', marginBottom: 24 }}>
            <Icon name="search" size={16} style={{ position: 'absolute', left: 14, top: 14, color: 'var(--text-muted)' }} />
            <input className="input" style={{ paddingLeft: 42, height: 48 }} placeholder="Search for answers..." value={search} onChange={event => setSearch(event.target.value)} />
          </div>

          <div className="card" style={{ padding: '4px 24px' }}>
            {visibleFaqs.length === 0 ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <p className="body" style={{ color: 'var(--text-muted)' }}>No articles match "{search}".</p>
                <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => setSupportOpen(true)}>Ask support</button>
              </div>
            ) : visibleFaqs.map((faq, index) => (
              <div key={`${faq.cat}-${faq.q}`} className="faq-item" onClick={() => setOpenId(openId === index ? null : index)}>
                <div className="faq-q">
                  <span>{faq.q}</span>
                  <Icon name="chevronDown" size={16} style={{ color: 'var(--text-muted)', transition: 'transform 200ms ease', transform: openId === index ? 'rotate(180deg)' : 'none' }} />
                </div>
                {openId === index && <div className="faq-a fade-in">{faq.a}</div>}
              </div>
            ))}
          </div>
        </section>
      </div>

      {supportOpen && <SupportModal onClose={() => setSupportOpen(false)} />}
    </div>
  );
}

function SupportModal({ onClose }) {
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={event => event.stopPropagation()}>
        <div className="row between" style={{ marginBottom: 16 }}>
          <h3 className="h2">Contact support</h3>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6, height: 28, width: 28 }} onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <p className="caption" style={{ marginBottom: 18 }}>Tell us what happened. This first MVP keeps the form local until support routing is connected.</p>
        <label className="label">Topic</label>
        <select className="select" defaultValue="bug">
          <option value="bug">Bug report</option>
          <option value="billing">Billing question</option>
          <option value="account">Account access</option>
          <option value="other">Something else</option>
        </select>
        <label className="label" style={{ marginTop: 14 }}>Describe your issue</label>
        <textarea className="textarea" placeholder="What were you trying to do? What happened instead?" rows={5} />
        <div className="row" style={{ gap: 8, marginTop: 20 }}>
          <button className="btn btn-secondary btn-block" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-block" onClick={onClose}>Send message</button>
        </div>
      </div>
    </div>
  );
}

export function NotificationsPanel({ onClose, onNavigate, onUnreadChange, onOpenAssistant }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unreadCount = items.filter(item => item.unread).length;

  useEffect(() => {
    let cancelled = false;
    async function loadNotifications() {
      try {
        setLoading(true);
        setError(null);
        await api.notifications.runSmartRecommendations().catch(() => null);
        const response = await api.notifications.list({ limit: 25 });
        if (cancelled) return;
        const mapped = (response.notifications || []).map(mapNotification);
        setItems(mapped);
        onUnreadChange?.(Number(response.unreadCount ?? mapped.filter(item => item.unread).length));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load notifications');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    loadNotifications();
    return () => {
      cancelled = true;
    };
  }, [onUnreadChange]);

  async function markAllRead() {
    try {
      await api.notifications.markAllRead();
      setItems(prev => prev.map(item => ({ ...item, unread: false })));
      onUnreadChange?.(0);
    } catch (err) {
      setError(err.message || 'Unable to mark notifications read');
    }
  }

  async function openNotification(item) {
    if (item.unread) {
      setItems(prev => prev.map(current => current.id === item.id ? { ...current, unread: false } : current));
      onUnreadChange?.(Math.max(0, unreadCount - 1));
      try {
        await api.notifications.markRead(item.id);
      } catch (err) {
        setError(err.message || 'Unable to mark notification read');
      }
    }
    if (item.assistantContext) {
      onOpenAssistant?.(item.assistantContext);
      onClose();
      return;
    }
    if (item.destination) {
      onNavigate(item.destination);
      onClose();
    }
  }

  return (
    <div className="notif-popover">
      <div className="row between" style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-soft)' }}>
        <div className="row" style={{ gap: 8 }}>
          <h3 className="h3">Notifications</h3>
          {unreadCount > 0 && <span className="chip chip-active" style={{ padding: '2px 8px', fontSize: 11 }}>{unreadCount}</span>}
        </div>
        <div className="row" style={{ gap: 4 }}>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, padding: '4px 8px', height: 28 }} disabled={!unreadCount} onClick={markAllRead}>Mark all read</button>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6, height: 28, width: 28 }} onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
      </div>
      <div className="scroll-y" style={{ flex: 1 }}>
        {loading && (
          <div className="notif-item">
            <div className="notif-icon" style={{ background: TONES.blue[0], color: TONES.blue[1] }}><Icon name="refresh" size={16} /></div>
            <div className="caption">Loading notifications...</div>
          </div>
        )}
        {!loading && error && (
          <div className="notif-item">
            <div className="notif-icon" style={{ background: TONES.red[0], color: TONES.red[1] }}><Icon name="alert" size={16} /></div>
            <div className="caption">{error}</div>
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="notif-item">
            <div className="notif-icon" style={{ background: TONES.green[0], color: TONES.green[1] }}><Icon name="checkCircle" size={16} /></div>
            <div className="caption">No notifications yet.</div>
          </div>
        )}
        {!loading && !error && items.map(item => {
          const [bg, fg] = TONES[item.tone] || TONES.blue;
          return (
            <div key={item.id} className={'notif-item ' + (item.unread ? 'unread' : '')} onClick={() => openNotification(item)}>
              <div className="notif-icon" style={{ background: bg, color: fg }}><Icon name={item.icon} size={16} /></div>
              <div className="col" style={{ flex: 1, gap: 2, minWidth: 0 }}>
                <div className="row between" style={{ gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{item.title}</div>
                  <div className="caption" style={{ flexShrink: 0 }}>{item.time}</div>
                </div>
                <div className="caption" style={{ lineHeight: 1.4 }}>{item.body}</div>
                <div className="notif-meta-row">
                  <span>{item.unread ? 'Unread' : 'Read'}</span>
                  {item.priority && <span className={`notif-priority is-${item.priority}`}>{item.priority}</span>}
                  {typeof item.creditCost === 'number' && item.creditCost > 0 && <span>uses plan quota</span>}
                </div>
                {(item.destination || item.assistantContext) && (
                  <button className="btn btn-secondary btn-sm" style={{ height: 26, fontSize: 12, padding: '0 10px', marginTop: 6, alignSelf: 'flex-start' }}>
                    {item.cta || (item.assistantContext ? 'Open in Ask AI' : 'Open')} <Icon name="arrowRight" size={12} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border-soft)', textAlign: 'center' }}>
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, color: 'var(--p-300)' }} onClick={() => { onNavigate('settings'); onClose(); }}>Notification settings</button>
      </div>
    </div>
  );
}

function mapNotification(notification) {
  const meta = notification.metadata || {};
  const type = notification.type || 'info';
  const assistantAction = meta.assistantAction && typeof meta.assistantAction === 'object' ? meta.assistantAction : null;
  const assistantParams = assistantAction?.params && typeof assistantAction.params === 'object' ? assistantAction.params : {};
  const entityType = typeof meta.entityType === 'string' ? meta.entityType : meta.affectedEntity?.type;
  const entityId = typeof meta.entityId === 'string' ? meta.entityId : meta.affectedEntity?.id;
  const assistantIntent = typeof meta.assistantIntent === 'string' ? meta.assistantIntent : assistantAction?.type;
  const assistantContext = meta.destination === 'assistant' || assistantIntent ? {
    type: 'notification',
    id: notification.id,
    notificationId: notification.id,
    recommendationId: typeof meta.recommendationId === 'string' ? meta.recommendationId : null,
    assistantIntent: assistantIntent || 'notification.open_recommendation',
    inputSummary: assistantParams.inputSummary || meta.reason || notification.message || notification.title,
    entityType: entityType || null,
    entityId: entityId || null,
    platform: assistantParams.platform || (entityType === 'platform' ? entityId : null),
    contentId: assistantParams.contentId || (entityType === 'contentItem' ? entityId : null),
    planLengthDays: assistantParams.planLengthDays || null,
    cta: typeof meta.cta === 'string' ? meta.cta : null,
    priority: typeof meta.priority === 'string' ? meta.priority : null,
    creditCost: typeof meta.creditCost === 'number' ? meta.creditCost : null,
  } : null;
  return {
    id: notification.id,
    type,
    title: notification.title || 'Notification',
    body: notification.message || '',
    time: formatNotificationTime(notification.createdAt),
    unread: notification.status === 'unread',
    icon: notificationIcon(type),
    tone: notificationTone(type),
    destination: typeof meta.destination === 'string' && meta.destination !== 'assistant' ? meta.destination : notificationDestination(type, notification.message),
    cta: typeof meta.cta === 'string' ? meta.cta : null,
    priority: typeof meta.priority === 'string' ? meta.priority : null,
    creditCost: typeof meta.creditCost === 'number' ? meta.creditCost : null,
    assistantContext
  };
}

function notificationTone(type) {
  if (['success', 'published'].includes(type)) return 'green';
  if (['warning', 'warn', 'token_expiring', 'token_expired'].includes(type)) return 'amber';
  if (['error', 'failed'].includes(type)) return 'red';
  if (['ai', 'content_generated', 'analytics_recommendation', 'smart_recommendation'].includes(type)) return 'purple';
  return 'blue';
}

function notificationIcon(type) {
  if (['success', 'published'].includes(type)) return 'checkCircle';
  if (['warning', 'warn', 'token_expiring', 'token_expired'].includes(type)) return 'alert';
  if (['ai', 'content_generated', 'analytics_recommendation', 'smart_recommendation'].includes(type)) return 'sparkles';
  if (type === 'telegram') return 'telegram';
  return 'bell';
}

function notificationDestination(type, message = '') {
  const text = `${type} ${message}`.toLowerCase();
  if (text.includes('token') || text.includes('social') || text.includes('telegram')) return 'social';
  if (text.includes('billing') || text.includes('credit')) return 'billing';
  if (text.includes('analytics') || text.includes('recommendation') || text.includes('performance')) return 'brand';
  if (text.includes('draft') || text.includes('content')) return 'content';
  return null;
}

function formatNotificationTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'Just now';
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  return formatAppDate(date, { month: 'short', day: 'numeric' });
}

export function NotFound({ onNavigate }) {
  return (
    <div className="notfound-shell cosmic-bg">
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, maxWidth: 560 }}>
        <div className="notfound-num">404</div>
        <h2 className="h1" style={{ marginTop: -10, marginBottom: 12 }}>Page not found</h2>
        <p className="body" style={{ color: 'var(--text-muted)', marginBottom: 32 }}>The page does not exist or was moved.</p>
        <div className="row" style={{ justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-lg" onClick={() => onNavigate('help')}>Get help</button>
          <button className="btn btn-primary btn-lg" onClick={() => onNavigate('dashboard')}><Icon name="home" size={14} /> Go to Dashboard</button>
        </div>
      </div>
    </div>
  );
}
